import { useParams } from 'react-router-dom';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import useCampaignData from './useCampaignData';
import useCampaignTypesData from './useCampaignTypesData';
import usePayoutsCampaignData from './usePayoutsCampaignData';
import type { DateRange, ShowFilter } from '@/types';
import { usePageMetadata } from '@/hooks';

/**
 * Custom hook to manage all data fetching for the Campaign Types Modal
 * @returns Object containing refetch functions for prefetching data
 */
const useCampaignTypesModalData = (
  modalDateRange?: DateRange,
  modalSelectedShow?: ShowFilter
) => {
  const { year, month } = useParams();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { page: pageType } = usePageMetadata();
  const { filters } = useFilterContext();

  if (!pageType) throw new Error('Page type is required');

  const selectedShow = modalSelectedShow || (filters.selectedShows[0] ?? 'all');
  const dateRange = modalDateRange || filters.dateRange;

  // Earnings queries
  const {
    refetch: fetchCampaigns,
    data: campaignData,
    isPending: isCampaignsLoading,
  } = useCampaignData(selectedNetwork, dateRange, pageType === 'earnings', selectedShow);

  const { refetch: fetchCampaignTypes } = useCampaignTypesData(
    selectedNetwork,
    dateRange,
    pageType === 'earnings'
  );

  // Payouts queries
  const {
    refetch: fetchPayoutsCampaigns,
    data: payoutsData,
    isPending: isPayoutsLoading,
  } = usePayoutsCampaignData(selectedNetwork, year, month, pageType === 'payouts');

  const handlePrefetch = () => {
    if (pageType === 'earnings') {
      fetchCampaigns();
      fetchCampaignTypes();
    } else {
      fetchPayoutsCampaigns();
    }
  };

  const data = pageType === 'earnings' ? campaignData?.campaigns_by_category : payoutsData;
  const isLoading = pageType === 'earnings' ? isCampaignsLoading : isPayoutsLoading;

  return {
    data,
    handlePrefetch,
    isLoading,
    pageType,
    selectedNetwork,
  };
};

export default useCampaignTypesModalData;
